import React, { useEffect, useState, useContext } from "react";
import { Table, Alert, Container, Card, Button, Badge, Form, Row, Col} from "react-bootstrap";
import { InputGroup, InputGroupText, Input, FormGroup } from "reactstrap";
import { ThemeContext } from "contexts/ThemeContext.jsx";
import { serverHost } from "variables/serverdata";
import HusariaHeader from "../components/HusariaHeader";

const EventGuard = () => {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === "dark";
    
    const [events, setEvents] = useState([]);
    const [filteredEvents, setFilteredEvents] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [filterType, setFilterType] = useState("all"); // all, IP, Auth
    const [statusFilter, setStatusFilter] = useState("all"); // all, blocked, allowed
    const [searchTerm, setSearchTerm] = useState("");

    const fetchEventGuardData = async () => {
        setLoading(true);
        try {
            const site = JSON.parse(localStorage.getItem("selectedSite"));
            const url = `${serverHost}/event_logs/${site.site_uuid}`
            const response = await fetch(url, {
                method: "GET"
            });
            if (!response.ok) throw new Error("Failed to fetch event guard data");
            console.log("Refreshed event guard data");
            const data = await response.json();
            console.log(data.result);
            setError(null)
            setEvents(data.result || []);
        } catch (err) {
            setError(err.message);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchEventGuardData();
        
        // Auto-refresh every 10 seconds
        const interval = setInterval(fetchEventGuardData, 10000);
        
        return () => clearInterval(interval);
    }, []);

    useEffect(() => {
        let filtered = events;
        
        // Filter by type (IP/Auth)
        if (filterType !== "all") {
            filtered = filtered.filter(event => 
                event.filter?.toLowerCase() === filterType.toLowerCase()
            );
        }
        
        // Filter by status (blocked/allowed)
        if (statusFilter !== "all") {
            filtered = filtered.filter(event => 
                event.status?.toLowerCase() === statusFilter.toLowerCase()
            );
        }
        
        // Search across all columns
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            filtered = filtered.filter(event => 
                event.hostname?.toLowerCase().includes(term) ||
                event.date?.toLowerCase().includes(term) ||
                event.time?.toLowerCase().includes(term) ||
                event.filter?.toLowerCase().includes(term) ||
                event.ip_address?.toLowerCase().includes(term) ||
                event.extension?.toLowerCase().includes(term) ||
                event.user_agent?.toLowerCase().includes(term) ||
                event.log_status?.toLowerCase().includes(term)
            );
        }
        
        setFilteredEvents(filtered);
    }, [events, filterType, statusFilter, searchTerm]);

    // Calculate statistics
    const totalEvents = events.length;
    const ipEvents = events.filter(e => e.filter?.toLowerCase() === "ip").length;
    const authEvents = events.filter(e => e.filter?.toLowerCase() === "auth").length;
    const blockedEvents = events.filter(e => e.status?.toLowerCase() === "blocked").length;
    const allowedEvents = events.filter(e => e.status?.toLowerCase() === "allowed").length;

    const getFilterBadge = (filter) => {
        const filterLower = filter?.toLowerCase();
        if (filterLower === "ip") return <Badge bg="primary">IP</Badge>;
        if (filterLower === "auth") return <Badge bg="info">Auth</Badge>;
        return <Badge bg="secondary">{filter || "Unknown"}</Badge>;
    };

    const getStatusBadge = (status) => {
        const statusLower = status?.toLowerCase();
        if (statusLower === "blocked") return <Badge bg="danger">Blocked</Badge>;
        if (statusLower === "allowed") return <Badge bg="success">Allowed</Badge>;
        return <Badge bg="warning" text="dark">{status || "Unknown"}</Badge>;
    };

    const info_style = {
        backgroundColor: isDark ? "#1f2937" : "#fff", 
        borderRadius: "1rem",
        overflow: "hidden", 
    }

    return (
        <>
            <HusariaHeader name="Event Guard" parentName="HUCS" padding="9rem"/>

            <Container fluid className="mt--8">
                {/* Statistics Cards */}
                <Row className="mb-4">
                    <Col lg="3" md="6" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                            Total Events
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700 }}>
                                            {totalEvents}
                                        </h2>
                                    </div>
                                    <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                                        <i class="fas fa-shield-alt"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg="3" md="6" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                            IP Events
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700, color: "#007bff" }}>
                                            {ipEvents}
                                        </h2>
                                    </div>
                                    <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                                        <i class="fas fa-globe"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg="3" md="6" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                            Auth Events
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700, color: "#17a2b8" }}>
                                            {authEvents}
                                        </h2>
                                    </div>
                                    <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                                        <i class="fas fa-lock"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>

                    <Col lg="3" md="6" className="mb-3">
                        <Card
                            className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}
                            style={info_style}
                        >
                            <Card.Body>
                                <div className="d-flex justify-content-between align-items-center">
                                    <div>
                                        <p className="mb-0 text-muted" style={{ fontSize: "0.875rem" }}>
                                            Blocked / Allowed
                                        </p>
                                        <h2 className="mb-0" style={{ fontWeight: 700 }}>
                                            <span style={{ color: "#dc3545" }}>{blockedEvents}</span>
                                            {" / "}
                                            <span style={{ color: "#28a745" }}>{allowedEvents}</span>
                                        </h2>
                                    </div>
                                    <div style={{ fontSize: "2.5rem", opacity: 0.8 }}>
                                        <i class="fas fa-balance-scale"></i>
                                    </div>
                                </div>
                            </Card.Body>
                        </Card>
                    </Col>
                </Row>

                {/* Event Guard Table */}
                <Card className={`shadow-sm ${isDark ? "bg-dark text-light border-dark" : ""}`}>
                    <Card.Header
                        className="d-flex justify-content-between align-items-center flex-wrap"
                        style={{
                            backgroundColor: isDark ? "#1f2937" : "#f0f0f0",
                            color: isDark ? "#e2e8f0" : "#111",
                        }}
                    >
                        <div className="d-flex align-items-center" >
                            <h4 className="mb-2 mb-md-0">
                                <Badge bg="primary" className="ms-2" style={{ marginLeft: "0.5rem", marginRight: "1rem" }}>
                                    {filteredEvents.length} Events
                                </Badge>
                            </h4>
                            <Form className="navbar-search navbar-search-light form-inline search-border">
                            <FormGroup className="mb-0">
                            <InputGroup className="input-group-alternative input-group-merge" >
                                <InputGroupText>
                                <i className="fas fa-search search-icon"/>
                                </InputGroupText>
                                <Input
                                className="table-search-bar form-control "
                                placeholder="Search"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                />
                            </InputGroup>
                            </FormGroup>
                        </Form>
                        </div>
                        <div className="d-flex gap-2 flex-wrap align-items-center">  
                            {/* Filter Type Buttons */}
                            <Button
                                variant={filterType === "all" ? "primary" : "outline-primary"}
                                size="sm"
                                
                                onClick={() => setFilterType("all")}
                            >
                                All
                            </Button>
                            <Button
                                variant={filterType === "IP" ? "primary" : "outline-primary"}
                                size="sm"
                                onClick={() => setFilterType("IP")}
                            >
                                IP ({ipEvents})
                            </Button>
                            <Button
                                variant={filterType === "Auth" ? "info" : "outline-info"}
                                size="sm"
                                onClick={() => setFilterType("Auth")}
                            >
                                Auth ({authEvents})
                            </Button>

                            {/* Status Filter Buttons */}
                            <div className="border-start ps-2" style={{paddingRight: "0.5rem"}}>
                                <Button
                                    variant={statusFilter === "all" ? "light" : "outline-light"}
                                    size="sm"
                                    onClick={() => setStatusFilter("all")}
                                >
                                    All Status
                                </Button>
                                <Button
                                    variant={statusFilter === "blocked" ? "danger" : "outline-danger"}
                                    size="sm"
                                    className="ms-1"
                                    onClick={() => setStatusFilter("blocked")}
                                >
                                    Blocked ({blockedEvents})
                                </Button>
                                <Button
                                    variant={statusFilter === "allowed" ? "success" : "outline-success"}
                                    size="sm"
                                    className="ms-1"
                                    onClick={() => setStatusFilter("allowed")}
                                >
                                    Allowed ({allowedEvents})
                                </Button>
                            </div>

                            {/* Refresh */}
                            <Button
                                variant="info"
                                size="sm"
                                onClick={fetchEventGuardData}
                                disabled={loading}
                            >
                                {loading ? "Loading..." : "Refresh"}
                            </Button>
                        </div>
                    </Card.Header>

                    <Card.Body style={{ padding: 0, backgroundColor: isDark ? "#1f2937" : "#fff" }}>
                        {error ? (
                            <Alert variant="danger" className="m-3">
                                {error}
                            </Alert>
                        ) : filteredEvents.length === 0 ? (
                            <div
                                className="text-center py-5"
                                style={{ color: isDark ? "#9ca3af" : "#6c757d" }}
                            >
                                <h5>No events found</h5>
                                <p className="mb-0">
                                    {searchTerm 
                                        ? "No events match your search criteria." 
                                        : "There are currently no event guard logs."}
                                </p>
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <Table
                                    striped
                                    hover
                                    responsive
                                    bordered
                                    className="mb-0"
                                    style={{
                                        minWidth: "1400px",
                                        backgroundColor: isDark ? "#1f2937" : "#fff",
                                        color: isDark ? "#e2e8f0" : "#212529",
                                    }}
                                >
                                    <thead
                                        style={{
                                            backgroundColor: isDark ? "#374151" : "#f8f9fa",
                                            fontWeight: "bold",
                                            color: isDark ? "#e2e8f0" : "#212529",
                                        }}
                                    >
                                        <tr>
                                            <th className="text-center">Hostname</th>
                                            <th className="text-center">Date</th>
                                            <th className="text-center">Time</th>
                                            <th className="text-center">Filter</th>
                                            <th className="text-center">Address</th>
                                            <th className="text-center">Extension</th>
                                            <th className="text-center">User Agent</th>
                                            <th className="text-center">Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {filteredEvents.map((event, index) => (
                                            <tr key={index} style={{ color: isDark ? "#e2e8f0" : "#212529" }}>
                                                <td className="text-center" style={{ fontWeight: 500 }}>
                                                    {event.hostname || "-"}
                                                </td>
                                                <td className="text-center">{event.date || "-"}</td>
                                                <td className="text-center" style={{ fontFamily: "monospace" }}>
                                                    {event.time || "-"}
                                                </td>
                                                <td className="text-center">
                                                    {getFilterBadge(event.filter)}
                                                </td>
                                                <td className="text-center" style={{ fontFamily: "monospace" }}>
                                                    {event.address || "-"}
                                                </td>
                                                <td className="text-center" style={{ fontWeight: 500 }}>
                                                    {event.extension || "-"}
                                                </td>
                                                <td className="text-center" style={{ maxWidth: "300px", overflow: "hidden", textOverflow: "ellipsis" }}>
                                                    {event.user_agent || "-"}
                                                </td>
                                                <td className="text-center">
                                                    {getStatusBadge(event.status)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Card.Body>
                </Card>
            </Container>
        </>
    );
};

export default EventGuard;